import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ruler, Cube, CurrencyCircleDollar, Package, Lightning, ShieldCheck, CreditCard, PencilRuler, Calculator, Stack, CheckSquare, Wrench, Download as DownloadIcon, Quotes, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { plansAPI, testimonialsAPI } from '../lib/api';
import PlanCard from '../components/plans/PlanCard';

const HomePage = () => {
  const [popularPlans, setPopularPlans] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  useEffect(() => {
    plansAPI.getPopular(4).then(r => setPopularPlans(r.data)).catch(() => {});
    testimonialsAPI.getTestimonials().then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(() => {
      setTestimonialIdx(prev => (prev + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [testimonials]);

  const categories = [
    { name: 'By Size', icon: Ruler, description: 'Find plans by area, bedrooms, or floors', link: '/plans' },
    { name: 'By Type', icon: Cube, description: 'Browse A, B, C, D, and E-type plans', link: '/plans' },
    { name: 'By Budget', icon: CurrencyCircleDollar, description: 'Plans organized by price range', link: '/affordable-house-plans' },
    { name: 'All Plans', icon: Package, description: 'View our complete collection', link: '/plans' }
  ];

  const trustBadges = [
    { icon: Lightning, title: 'Instant Digital Delivery' },
    { icon: ShieldCheck, title: 'SA NBR Compliant' },
    { icon: CreditCard, title: 'Payment Options' },
    { icon: Stack, title: 'Drawing Sets' },
    { icon: PencilRuler, title: 'Custom Design' },
    { icon: Calculator, title: 'Cost Estimates' }
  ];

  const howItWorks = [
    { icon: CheckSquare, title: 'Choose', desc: 'Choose your plan' },
    { icon: Wrench, title: 'Configure', desc: 'Select your drawing package' },
    { icon: CreditCard, title: 'Purchase', desc: 'Make your payment' },
    { icon: DownloadIcon, title: 'Download', desc: 'Download your plans' }
  ];

  const currentTestimonial = testimonials[testimonialIdx];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero */}
      <section
        className="relative h-[600px] flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 37, 64, 0.85), rgba(10, 37, 64, 0.85)), url('https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwZmxvb3IlMjBwbGFuJTIwYmx1ZXByaW50fGVufDB8fHx8MTc4NDkxNjk2OHww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1
            className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            YOUR DREAM HOME STARTS WITH THE RIGHT PLAN
          </motion.h1>
          <motion.p
            className="text-lg md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Professionally designed house plans for South African homeowners. Browse by bedrooms, size, style, stand and budget.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link
              to="/plans"
              className="inline-flex items-center space-x-2 bg-accent text-white px-8 py-4 rounded text-lg font-bold hover:-translate-y-1 transition-transform"
              data-testid="browse-plans-btn"
            >
              <span>Browse Plans</span>
              <ArrowRight size={24} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section className="bg-white py-12 border-b border-border" data-testid="how-it-works">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-primary text-center mb-10" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
            Designed to take you from idea to construction
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 relative">
            {howItWorks.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="flex flex-col items-center text-center relative" data-testid={`how-step-${step.title.toLowerCase()}`}>
                  <div className="flex items-center justify-center w-16 h-16 bg-accent text-white rounded-full mb-4 relative z-10">
                    <Icon size={28} weight="bold" />
                  </div>
                  <div className="text-xs font-mono text-text_muted mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>STEP {i + 1}</div>
                  <h3 className="text-lg font-bold text-primary mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{step.title}</h3>
                  <p className="text-sm text-text_muted">{step.desc}</p>
                  {i < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-[2px] bg-border" style={{ zIndex: 0 }}></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-surface border-b border-border py-8" data-testid="trust-banner">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {trustBadges.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div
                  key={badge.title}
                  className="flex flex-col items-center text-center px-2"
                  data-testid={`trust-badge-${badge.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <Icon size={32} weight="duotone" className="text-accent mb-2" />
                  <h4 className="text-sm font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    {badge.title}
                  </h4>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Plans */}
      {popularPlans.length > 0 && (
        <section className="py-12 bg-white" data-testid="popular-plans">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                Popular Plans
              </h2>
              <Link to="/plans" className="text-sm font-medium text-accent hover:underline flex items-center">
                View all <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularPlans.map(plan => (
                <PlanCard key={plan.plan_id} plan={plan} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="py-16 bg-surface" data-testid="categories-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
            Find Your Perfect Plan
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.name}
                  to={category.link}
                  className="block border border-border bg-white p-8 hover:-translate-y-1 transition-transform group"
                  data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon size={48} weight="duotone" className="text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{category.name}</h3>
                  <p className="text-text_muted text-sm mb-4">{category.description}</p>
                  <div className="flex items-center text-accent text-sm font-medium group-hover:translate-x-2 transition-transform">
                    <span>Explore</span>
                    <ArrowRight size={16} weight="bold" className="ml-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16" data-testid="features-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
              What You Get
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="border border-border p-6 bg-white">
                <h3 className="text-lg font-bold text-primary mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>File Formats</h3>
                <ul className="space-y-2 text-text_muted">
                  <li className="flex items-start"><span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0"></span><span>PDF format for easy viewing and printing</span></li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0"></span><span>CAD files for modifications (optional upgrade)</span></li>
                </ul>
              </div>
              <div className="border border-border p-6 bg-white">
                <h3 className="text-lg font-bold text-primary mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Drawing Sets</h3>
                <ul className="space-y-2 text-text_muted">
                  <li className="flex items-start"><span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0"></span><span>Architectural drawings</span></li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0"></span><span>Structural drawings (optional)</span></li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0"></span><span>Mechanical &amp; Electrical drawings (optional)</span></li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0"></span><span>Bill of quantities (optional)</span></li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0"></span><span>Local council submission set (optional)</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Strip */}
      {testimonials.length > 0 && (
        <section className="py-16 bg-primary text-white" data-testid="testimonials-strip">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Quotes size={40} weight="fill" className="text-accent mx-auto mb-6" />
              <AnimatePresence mode="wait">
                {currentTestimonial && (
                  <motion.div
                    key={currentTestimonial.testimonial_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                  >
                    <p className="text-xl md:text-2xl italic mb-6 leading-relaxed" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      "{currentTestimonial.testimonial}"
                    </p>
                    <div className="flex items-center justify-center space-x-4">
                      {currentTestimonial.image_url && (
                        <img
                          src={currentTestimonial.image_url}
                          alt={currentTestimonial.customer_name}
                          className="w-14 h-14 rounded-full object-cover border-2 border-accent"
                        />
                      )}
                      <div className="text-left">
                        <div className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{currentTestimonial.customer_name}</div>
                        {currentTestimonial.location && (
                          <div className="text-xs text-gray-300 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{currentTestimonial.location}</div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              {testimonials.length > 1 && (
                <div className="flex items-center justify-center space-x-2 mt-8">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setTestimonialIdx(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === testimonialIdx ? 'bg-accent' : 'bg-gray-500'}`}
                      aria-label={`Show testimonial ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
